import { format } from "date-fns";
import { useNavigate } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { JobStatusBadge } from "./job-status-badge";
import { IMPORT_PROVIDERS } from "@/lib/types/itsm-import";
import type { ImportJobResponse } from "@/lib/types/itsm-import";
import { useAllTenants } from "@/hooks/use-tenants";

interface ImportJobsTableProps {
  jobs: ImportJobResponse[];
}

export function ImportJobsTable({ jobs }: ImportJobsTableProps) {
  const navigate = useNavigate();
  const { data: tenants } = useAllTenants();

  const getTenantName = (id: string) => {
    return tenants?.find((t) => t.id === id)?.company ?? id.slice(0, 8);
  };

  if (jobs.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        No import jobs found.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Tenant</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Targets</TableHead>
            <TableHead>Succeeded</TableHead>
            <TableHead>Failed</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <TableRow
              key={job.id}
              className="cursor-pointer"
              onClick={() => { void navigate({ to: "/itsm-import/$jobId", params: { jobId: job.id }, search: { tenantId: job.tenant_id } }); }}
            >
              <TableCell>
                <JobStatusBadge status={job.status} />
              </TableCell>
              <TableCell className="text-sm">
                {getTenantName(job.tenant_id)}
              </TableCell>
              <TableCell className="text-sm">
                {job.source === "provider"
                  ? (IMPORT_PROVIDERS.find((p) => p.value === job.provider)?.label ?? job.provider ?? "Provider")
                  : "File"}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {[
                  job.export_csv && "CSV export",
                  job.create_tickets && "SD",
                  job.generate_insights && "Insights",
                ]
                  .filter(Boolean)
                  .join(" + ") || "Fetch only"}
              </TableCell>
              <TableCell className="text-sm text-green-600">
                {job.succeeded_tickets}
              </TableCell>
              <TableCell className={`text-sm ${job.failed_tickets_count > 0 ? "text-destructive" : ""}`}>
                {job.failed_tickets_count}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {format(new Date(job.created_at), "MMM d, HH:mm")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
